import { useTranslation } from 'next-i18next';

import { Box, List, Text } from '@chakra-ui/react';

import { usePluralisationTranslation } from '@nocowanie/common-ui';

import { apiTypes } from '@app/models';

import { RoomDetailsProps } from './room-details.props';

const RoomAmenities = ({ amenities }: { amenities: apiTypes.Amenity[] }) => {
    if (!amenities || amenities.length === 0) return null;

    return (
        <>
            {amenities.map(amenity => (
                <List.Item key={amenity.name} _marker={{ fontSize: '0.75rem' }}>
                    {amenity.option ? amenity.option.description : amenity.name}
                </List.Item>
            ))}
        </>
    );
};

const RoomBeds = ({ beds }: { beds: apiTypes.Bed[] }) => {
    if (!beds || beds.length === 0) return null;

    return (
        <>
            {beds.map(bed => (
                <List.Item key={bed.name} _marker={{ fontSize: '0.75rem' }}>
                    {`${bed.count}x`} {bed.name}
                </List.Item>
            ))}
        </>
    );
};

export const RoomDetails = ({
    apartment,
    simplifyTags = false,
    hideHeading = false,
    headingProps = {},
    ...props
}: RoomDetailsProps) => {
    const { t } = useTranslation('accommodation', {
        keyPrefix: 'apartmentDetails',
    });
    const pluralizeTranslation = usePluralisationTranslation('accommodation', 'apartmentDetails');

    const renderRoom = (room: any) => {
        if (!room) return null;

        const roomCapacity = room.beds?.reduce((acc: number, bed: apiTypes.Bed) => {
            return acc + bed.count * bed.adultCount;
        }, 0);

        const totalGuests = pluralizeTranslation(roomCapacity, 'roomCapacity');

        return (
            <>
                {simplifyTags ? (
                    <>
                        {room.beds && room.beds.length ? `(${t('for')} ${totalGuests})` : ''}
                        {room.beds.length ? ': ' : ''}
                        {room.beds &&
                            room.beds
                                .map((bed: apiTypes.Bed) => `${bed.count}x ` + bed.name)
                                .join(', ')}
                        {room.beds && room.beds.length ? ', ' : ''}
                        {room.amenities
                            .map((amenity: apiTypes.Amenity) =>
                                amenity.option ? amenity.option.description : amenity.name,
                            )
                            .join(', ')}
                    </>
                ) : (
                    <>
                        {room.beds && room.beds.length ? ` (${t('for')} ${totalGuests})` : null}
                        <List.Root pl={4}>
                            <RoomBeds beds={room.beds} />
                            <RoomAmenities amenities={room.amenities} />
                        </List.Root>
                    </>
                )}
            </>
        );
    };

    const renderRoomTypes = (roomTypes: apiTypes.RoomType[]) => {
        if (!roomTypes || roomTypes.length === 0) return null;

        return roomTypes.map(roomType => {
            return roomType?.rooms?.map((room, i) => {
                const roomCount = roomType?.rooms?.length;
                const isMultiRoom = roomCount && roomCount > 1;

                return (
                    <List.Item key={roomType.type.name + i} _marker={{ fontSize: '1rem' }}>
                        {isMultiRoom ? `${i + 1}. ` : null}
                        {roomType.type.name}
                        {simplifyTags && room?.amenities?.length && !room?.beds?.length
                            ? ': '
                            : ' '}
                        {renderRoom(room)}
                    </List.Item>
                );
            });
        });
    };

    return (
        <Box fontSize={'sm'} {...props}>
            {!hideHeading ? (
                <Text as={'strong'} fontSize={'lg'} {...headingProps}>
                    {t('heading')}
                </Text>
            ) : null}
            {apartment?.size ? (
                <Text>
                    {t('baseSize')} {apartment.size}m<sup>2</sup>
                </Text>
            ) : null}
            <List.Root pl={4}>
                {renderRoomTypes(apartment?.roomTypes as apiTypes.RoomType[])}
            </List.Root>
        </Box>
    );
};
